/**
 * API client Textprint — Sprint 3 back-office admin CommandesBoutique.
 * Brief SIM-PLATFORM-BOUTIQUE-2026-001 §B2 + brief extension §3.
 *
 * Endpoints : /v1/operations/textprint/commandes-boutique/…
 */
import { apiClient, unwrap, unwrapPaginated } from './client';
import type { Paginated } from './operations';
import type {
  CanalCommandeBoutique,
  CommandeBoutique,
  LigneCommandeBoutique,
  LivraisonBoutique,
  MethodePaiementBoutique,
  ModeLivraisonBoutique,
  PaiementBoutique,
  StatutCommandeBoutique,
  StatutPaiementBoutique,
} from '@/types';

const BASE = '/operations/textprint/commandes-boutique';

// ── Pagination commune ────────────────────────────────────────────────────
interface BasePaging {
  page?:  number;
  limit?: number;
}

// ══════════════════════════════════════════════════════════════════════════
// COMMANDES BOUTIQUE
// ══════════════════════════════════════════════════════════════════════════

export interface CommandeBoutiqueListParams extends BasePaging {
  statut?:   StatutCommandeBoutique;
  canal?:    CanalCommandeBoutique;
  clientId?: string;
  search?:   string;
  dateFrom?: string;
  dateTo?:   string;
}

export interface CreateLigneCommandeBoutiqueInlinePayload {
  produitId:          string;
  varianteId?:        string;
  designation?:       string;
  sku?:               string;
  quantite:           number;
  prixUnitaireCents?: number;
  remisePct?:         number;
  personnalisation?:  Record<string, unknown>;
  batUrl?:            string;
  batValide?:         boolean;
  notes?:             string;
}

export interface CreateCommandeBoutiquePayload {
  reference?:              string;
  canal?:                  CanalCommandeBoutique;
  clientId?:               string;
  emailCommande:           string;
  phoneCommande?:          string;
  nomCompletCommande:      string;
  raisonSocialeCommande?:  string;
  remiseClientCents?:      number;
  remiseCodeCents?:        number;
  fraisLivraisonCents?:    number;
  statut?:                 StatutCommandeBoutique;
  notesClient?:            string;
  notesInternes?:          string;
  dateLivraisonPrevue?:    string;
  lignes?:                 CreateLigneCommandeBoutiqueInlinePayload[];
}

export type UpdateCommandeBoutiquePayload = Partial<
  Omit<CreateCommandeBoutiquePayload, 'reference' | 'lignes' | 'statut' | 'emailCommande' | 'nomCompletCommande'>
> & {
  emailCommande?:      string;
  nomCompletCommande?: string;
};

export const commandesBoutiqueApi = {
  list: async (params: CommandeBoutiqueListParams = {}): Promise<Paginated<CommandeBoutique>> => {
    const r = await apiClient.get(BASE, { params });
    return unwrapPaginated<CommandeBoutique>(r);
  },
  get: async (id: string): Promise<CommandeBoutique> => {
    const r = await apiClient.get(`${BASE}/${id}`);
    return unwrap<CommandeBoutique>(r);
  },
  create: async (payload: CreateCommandeBoutiquePayload): Promise<CommandeBoutique> => {
    const r = await apiClient.post(BASE, payload);
    return unwrap<CommandeBoutique>(r);
  },
  update: async (id: string, payload: UpdateCommandeBoutiquePayload): Promise<CommandeBoutique> => {
    const r = await apiClient.patch(`${BASE}/${id}`, payload);
    return unwrap<CommandeBoutique>(r);
  },
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`${BASE}/${id}`);
  },
  changerStatut: async (id: string, statut: StatutCommandeBoutique): Promise<CommandeBoutique> => {
    const r = await apiClient.patch(`${BASE}/${id}/statut`, { statut });
    return unwrap<CommandeBoutique>(r);
  },
};

// ══════════════════════════════════════════════════════════════════════════
// LIGNES DE COMMANDE
// ══════════════════════════════════════════════════════════════════════════

export interface CreateLigneCommandeBoutiquePayload {
  produitId:          string;
  varianteId?:        string;
  designation?:       string;
  sku?:               string;
  quantite:           number;
  prixUnitaireCents?: number;
  remisePct?:         number;
  personnalisation?:  Record<string, unknown>;
  batUrl?:            string;
  batValide?:         boolean;
  notes?:             string;
}
export type UpdateLigneCommandeBoutiquePayload = Partial<Omit<CreateLigneCommandeBoutiquePayload, 'produitId' | 'varianteId' | 'sku'>>;

export const lignesCommandeBoutiqueApi = {
  create: async (commandeId: string, payload: CreateLigneCommandeBoutiquePayload): Promise<LigneCommandeBoutique> => {
    const r = await apiClient.post(`${BASE}/${commandeId}/lignes`, payload);
    return unwrap<LigneCommandeBoutique>(r);
  },
  update: async (
    commandeId: string, lineId: string, payload: UpdateLigneCommandeBoutiquePayload,
  ): Promise<LigneCommandeBoutique> => {
    const r = await apiClient.patch(`${BASE}/${commandeId}/lignes/${lineId}`, payload);
    return unwrap<LigneCommandeBoutique>(r);
  },
  remove: async (commandeId: string, lineId: string): Promise<void> => {
    await apiClient.delete(`${BASE}/${commandeId}/lignes/${lineId}`);
  },
};

// ══════════════════════════════════════════════════════════════════════════
// PAIEMENTS
// ══════════════════════════════════════════════════════════════════════════

export interface CreatePaiementBoutiquePayload {
  methode:      MethodePaiementBoutique;
  statut?:      StatutPaiementBoutique;
  montantCents: number;
  reference?:   string;
  cinetpayId?:  string;
  notes?:       string;
}

export interface UpdatePaiementBoutiquePayload {
  statut?:         StatutPaiementBoutique;
  methode?:        MethodePaiementBoutique;
  montantCents?:   number;
  reference?:      string;
  cinetpayId?:     string;
  dateValidation?: string;
  notes?:          string;
}

export const paiementsBoutiqueApi = {
  create: async (commandeId: string, payload: CreatePaiementBoutiquePayload): Promise<PaiementBoutique> => {
    const r = await apiClient.post(`${BASE}/${commandeId}/paiements`, payload);
    return unwrap<PaiementBoutique>(r);
  },
  update: async (
    commandeId: string, paiementId: string, payload: UpdatePaiementBoutiquePayload,
  ): Promise<PaiementBoutique> => {
    const r = await apiClient.patch(`${BASE}/${commandeId}/paiements/${paiementId}`, payload);
    return unwrap<PaiementBoutique>(r);
  },
};

// ══════════════════════════════════════════════════════════════════════════
// LIVRAISON (1:1 par commande — PUT idempotent)
// ══════════════════════════════════════════════════════════════════════════

export interface UpsertLivraisonBoutiquePayload {
  mode:            ModeLivraisonBoutique;
  adresseLigne1:   string;
  adresseLigne2?:  string;
  ville:           string;
  zone?:           string;
  pays?:           string;
  livreurNom?:     string;
  livreurPhone?:   string;
  trackingCode?:   string;
  dateExpedition?: string;
  dateLivraison?:  string;
  signatureClient?: string;
  notes?:          string;
}

export const livraisonBoutiqueApi = {
  createOrUpdate: async (commandeId: string, payload: UpsertLivraisonBoutiquePayload): Promise<LivraisonBoutique> => {
    const r = await apiClient.put(`${BASE}/${commandeId}/livraison`, payload);
    return unwrap<LivraisonBoutique>(r);
  },
};

// ══════════════════════════════════════════════════════════════════════════
// Helpers actions rapides
// ══════════════════════════════════════════════════════════════════════════

export const changerStatut = (id: string, statut: StatutCommandeBoutique) =>
  commandesBoutiqueApi.changerStatut(id, statut);
